/**
 * RedeemPage Component
 * Handles both manual code entry (/redeem) and direct URL redemption (/redeem/:code)
 * Displays code entry form and user's redemption history
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { RedemptionHistorySection } from '../components/redeem/RedemptionHistorySection';
import { CodeEntryForm } from '../components/redeem/CodeEntryForm';
import { RedeemConfirmationDialog } from '../components/redeem/RedeemConfirmationDialog';
import { RedemptionSuccessFeedback } from '../components/redeem/RedemptionSuccessFeedback';
import type { RedeemCodeResponse } from '../lib/types/redeemGroup';

export function RedeemPage() {
  const { code: urlCode } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [codeToRedeem, setCodeToRedeem] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [redemptionResult, setRedemptionResult] =
    useState<RedeemCodeResponse | null>(null);
  const [showSuccessFeedback, setShowSuccessFeedback] = useState(false);

  // Uppercase URL code once for reuse
  const uppercaseUrlCode = urlCode?.toUpperCase();

  // Auto-trigger confirmation dialog if code provided in URL
  useEffect(() => {
    if (uppercaseUrlCode) {
      setCodeToRedeem(uppercaseUrlCode);
      setShowConfirmDialog(true);
    }
  }, [uppercaseUrlCode]);

  // Handle code entry submission
  const handleCodeSubmit = (code: string) => {
    setCodeToRedeem(code);
    setShowConfirmDialog(true);
  };

  // Handle successful redemption
  const handleRedemptionSuccess = (result: RedeemCodeResponse) => {
    setRedemptionResult(result);
    setShowConfirmDialog(false);
    setShowSuccessFeedback(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setShowConfirmDialog(false);
    setCodeToRedeem(null);
  };

  // Handle success feedback close
  const handleSuccessClose = () => {
    setShowSuccessFeedback(false);
    setRedemptionResult(null);
    setCodeToRedeem(null);
    // Clear URL parameter if it exists to allow new code entry
    if (uppercaseUrlCode) {
      navigate('/redeem', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to Dashboard Link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4 group"
        >
          <svg
            className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to Dashboard
        </Link>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Redeem Code
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Enter your code</p>
        </div>

        {/* Success Feedback (shown after successful redemption) */}
        {showSuccessFeedback && redemptionResult && (
          <RedemptionSuccessFeedback
            result={redemptionResult}
            onClose={handleSuccessClose}
          />
        )}

        {/* Code Entry Form */}
        {!showSuccessFeedback && (
          <div className="mb-8">
            <CodeEntryForm
              onSubmit={handleCodeSubmit}
              initialCode={uppercaseUrlCode}
            />
          </div>
        )}

        {/* Redemption History */}
        <RedemptionHistorySection />

        {/* Confirmation Dialog */}
        {codeToRedeem && (
          <RedeemConfirmationDialog
            code={codeToRedeem}
            isOpen={showConfirmDialog}
            onClose={handleDialogClose}
            onSuccess={handleRedemptionSuccess}
          />
        )}
      </div>
    </div>
  );
}
